import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon";
import { Checkbox } from "@/components/Checkbox";
import { Button } from "@/components/Button";
import { ReaderContext } from "@PublicationReader/context";
import { PublicationI } from "@PublicationReader/@types/Publication";
import { downloadPdf } from "@PublicationReader/helpers/downloadPdf";
import { AppProps } from "@PublicationReader/App";

interface DownloadModalProps extends Pick<AppProps, "apiDownloadUrl"> {
  publication: PublicationI;
}

const ProgressMessage = () => {
  const { t } = useTranslation("common");
  const { downloadPdfProgress, setDownloadModalIsOpen } =
    React.useContext(ReaderContext);

  return (
    <div className="pubr-download-modal__download-msg">
      {downloadPdfProgress === "complete" ? (
        <span className="pubr-download-modal__icon-done">
          <Icon name="checkmark" />
        </span>
      ) : (
        <span className="pubr-download-modal__icon-spinner"></span>
      )}

      <p>
        {downloadPdfProgress === "complete"
          ? t("downloadModal.complete")
          : t("downloadModal.inProgress")}
      </p>

      <div className="flex flex-justify-center">
        <Button onClick={() => setDownloadModalIsOpen(false)}>
          {t("close")}
        </Button>
      </div>
    </div>
  );
};

const DownloadModal = ({ publication, apiDownloadUrl }: DownloadModalProps) => {
  const { t } = useTranslation("common");
  const {
    setDownloadModalIsOpen,
    downloadModalIsOpen,
    setDownloadPdfProgress,
    downloadPdfProgress,
  } = React.useContext(ReaderContext);
  const { sections } = publication;
  // For storing IDs of selected sections, used for generating PDF
  const [selectedSectionIds, setSelectedSectionIds] = React.useState<string[]>(
    []
  );

  const closeHandler = React.useCallback(() => {
    setDownloadModalIsOpen(false);
  }, [downloadModalIsOpen, setDownloadModalIsOpen]);

  React.useEffect(() => {
    document.body.style.overflow = downloadModalIsOpen ? "hidden" : "auto";

    if (!downloadModalIsOpen) {
      // Reset download view
      setDownloadPdfProgress("idle");
      setSelectedSectionIds([]);
    }
  }, [downloadModalIsOpen]);

  // Add or remove ID from selectedSectionIds when checkbox is checked/unchecked
  const handleSectionCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked, value } = event.target;

    setSelectedSectionIds(
      checked
        ? [...selectedSectionIds, value]
        : selectedSectionIds.filter((id) => id !== value)
    );
  };

  const handleDownload = async () => {
    setDownloadPdfProgress("inProgress");

    const allSectionsSelected = selectedSectionIds.length === sections.length;
    const sectionsQuery = !allSectionsSelected
      ? `&chapters=${selectedSectionIds.join()}`
      : "";

    await downloadPdf(apiDownloadUrl, publication, sectionsQuery);
    setDownloadPdfProgress("complete");
  };

  return (
    <div
      className={classNames(
        "pubr-download-modal",
        downloadModalIsOpen && "pubr-download-modal--open"
      )}
    >
      <div className="pubr-download-modal__inner">
        <button
          className="pubr-icon-button pubr-download-modal__close-btn"
          onClick={closeHandler}
          aria-label={t("closePublication") as string}
        >
          <Icon name="cross" />
        </button>
        <h2 className="pubr-download-modal__title">
          {t("downloadPublication")}
        </h2>

        {downloadPdfProgress === "idle" ? (
          <>
            <div className="flex flex-justify-center">
              <Checkbox
                controlled
                label={t("downloadModal.selectAllSections") as string}
                isChecked={selectedSectionIds.length === sections.length}
                onChange={(event) =>
                  setSelectedSectionIds(
                    event.target.checked
                      ? [...sections.map((s) => s.sectionId)]
                      : []
                  )
                }
              />
            </div>

            <ul className="pubr-download-modal__sections">
              {sections.map((s) => (
                <li
                  className="pubr-download-modal__sections-item"
                  key={s.sectionId}
                >
                  <Checkbox
                    controlled
                    label={s.sectionHeading}
                    value={s.sectionId}
                    isChecked={selectedSectionIds.includes(s.sectionId)}
                    onChange={(event) => handleSectionCheckboxChange(event)}
                  />
                </li>
              ))}
            </ul>

            <div className="flex flex-justify-center h-margin-top-32">
              <Button
                disabled={selectedSectionIds.length === 0}
                onClick={() => handleDownload()}
              >
                {t("download")}
              </Button>
            </div>
          </>
        ) : (
          <ProgressMessage />
        )}
      </div>
    </div>
  );
};

export { DownloadModal };
